import {
  Alert,
  Badge,
  Collapse,
  Empty,
  Input,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  DownloadOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
} from "@ant-design/icons";
import React, { useEffect, useMemo, useState } from "react";

import axios from "../../utils/axios";
import IconButton from "../utils/IconButton";
import PageEntete from "../utils/PageEntete";
import {
  anomaliesOf,
  buildDirectoryCsv,
  computeAnomalies,
  contactsOf,
  csvFilename,
  filterRows,
  flattenGroups,
  groupLabel,
  groupRows,
  sanitizeRows,
} from "./directoryUtils";

const { Panel } = Collapse;
const DASH = "—";

function downloadCsv(content, filename) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

function Contacts({ row }) {
  const contacts = contactsOf(row);
  if (contacts.length === 0) return DASH;
  return (
    <div>
      {contacts.map((c, i) => (
        <div key={i}>
          {c.role && <Tag>{c.role}</Tag>}
          {c.nom && <span>{c.nom} </span>}
          {c.email && <a href={`mailto:${c.email}`}>{c.email}</a>}
          {c.telephone && <span> {c.telephone}</span>}
        </div>
      ))}
    </div>
  );
}

function Directory() {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState();
  const [server, setServer] = useState();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [openKeys, setOpenKeys] = useState(null); // null = tout deplie

  useEffect(() => {
    let cancelled = false;
    // Un seul GET : on charge tout (desactives inclus), le filtre actif est cote front.
    axios
      .get("/api/directory", { params: { includeInactive: 1 } })
      .then((res) => {
        if (!cancelled) setRows(sanitizeRows(res.data));
      })
      .catch(() => {
        if (!cancelled) setError("Impossible de charger l'annuaire.");
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Anomalies calculees sur le perimetre actif/inactif choisi (avant recherche)
  const scope = useMemo(
    () => filterRows(rows, { includeInactive }),
    [rows, includeInactive]
  );
  const anomalies = useMemo(() => computeAnomalies(scope), [scope]);

  const groupOptions = useMemo(
    () =>
      groupRows(scope).map((g) => ({ value: g.name, label: g.name })),
    [scope]
  );
  const serverOptions = useMemo(
    () =>
      Array.from(
        new Set(
          scope.map((r) => String(r.serveur || "").trim()).filter(Boolean)
        )
      )
        .sort((a, b) => a.localeCompare(b))
        .map((s) => ({ value: s, label: s })),
    [scope]
  );

  const filtered = useMemo(
    () => filterRows(rows, { search, group, server, includeInactive }),
    [rows, search, group, server, includeInactive]
  );
  const groups = useMemo(() => groupRows(filtered), [filtered]);

  const columns = [
    { key: "num_env", title: "N° env", dataIndex: "num_env", width: 110 },
    {
      key: "finess",
      title: "FINESS",
      dataIndex: "finess",
      width: 130,
      render: (v) => (v === null || v === undefined || v === "" ? "" : String(v)),
    },
    {
      key: "libelle",
      title: "Libellé",
      dataIndex: "libelle",
      render: (v, row) => (
        <Space size={4} wrap>
          <span>{v}</span>
          {row.actif === false && <Tag color="default">désactivé</Tag>}
          {(() => {
            const a = anomaliesOf(row, anomalies);
            return (
              <>
                {a.multiServer && (
                  <Tooltip title="Ce N° env est présent sur plusieurs serveurs">
                    <Tag color="orange">code multi-serveurs</Tag>
                  </Tooltip>
                )}
                {a.multiGroup && (
                  <Tooltip title="Ce N° env est présent dans plusieurs groupes">
                    <Tag color="volcano">code multi-groupes</Tag>
                  </Tooltip>
                )}
                {a.noGroup && (
                  <Tooltip title="Environnement sans groupe rattaché">
                    <Tag color="gold">sans groupe</Tag>
                  </Tooltip>
                )}
              </>
            );
          })()}
        </Space>
      ),
    },
    {
      key: "serveur",
      title: "Serveur",
      dataIndex: "serveur",
      width: 180,
      render: (v) => v || DASH,
    },
    {
      key: "contacts",
      title: "Contacts",
      width: 380,
      render: (_, row) => <Contacts row={row} />,
    },
  ];

  const handleExport = () => {
    downloadCsv(
      buildDirectoryCsv(flattenGroups(groups)),
      csvFilename(new Date(), { search, group, server, includeInactive })
    );
  };

  if (!loaded) {
    return (
      <div className="directory" style={{ textAlign: "center", padding: 50 }}>
        <Spin tip="Chargement de l'annuaire..." />
      </div>
    );
  }

  const allKeys = groups.map((g) => g.name);
  const activeKey = openKeys === null ? allKeys : openKeys;

  return (
    <div className="directory cti-page">
      <PageEntete
        title="Annuaire des clients"
        actions={
          <IconButton
            type="primary"
            title="Export CSV"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={filtered.length === 0}
          />
        }
      />
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <div className="cti-bar">
        <Input.Search
          allowClear
          placeholder="Code, FINESS, libellé, serveur, contact..."
          style={{ width: 340 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          allowClear
          showSearch
          placeholder="Groupe"
          style={{ width: 220 }}
          options={groupOptions}
          value={group}
          onChange={setGroup}
        />
        <Select
          allowClear
          showSearch
          placeholder="Serveur"
          style={{ width: 220 }}
          options={serverOptions}
          value={server}
          onChange={setServer}
        />
        <span style={{ whiteSpace: "nowrap" }}>
          <Switch
            checked={includeInactive}
            onChange={setIncludeInactive}
            aria-label="Inclure les désactivés"
          />{" "}
          Inclure les désactivés
        </span>
        <IconButton
          className="cti-bar-end"
          title="Tout déplier"
          icon={<PlusSquareOutlined />}
          onClick={() => setOpenKeys(allKeys)}
          disabled={!groups.length}
        />
        <IconButton
          title="Tout replier"
          icon={<MinusSquareOutlined />}
          onClick={() => setOpenKeys([])}
          disabled={!groups.length}
        />
      </div>
      <div className="cti-panel">
      {groups.length === 0 ? (
        <Empty description="Aucun environnement" />
      ) : (
        <Collapse
          activeKey={activeKey}
          onChange={(keys) => setOpenKeys(Array.isArray(keys) ? keys : [keys])}
        >
          {groups.map((g) => (
            <Panel
              key={g.name}
              header={
                <Space>
                  <strong>{g.name}</strong>
                  <Badge
                    count={`${g.envCount} env`}
                    style={{ backgroundColor: "var(--cti-blue)" }}
                  />
                  <Badge
                    count={`${g.serverCount} serveur${
                      g.serverCount > 1 ? "s" : ""
                    }`}
                    style={{ backgroundColor: "#52c41a" }}
                  />
                </Space>
              }
            >
              <Table
                size="small"
                pagination={false}
                columns={columns}
                dataSource={g.rows}
                rowKey={(r) =>
                  `${r.id_finess ?? r.id ?? ""}|${r.num_env}|${r.serveur ?? ""}|${groupLabel(r)}`
                }
              />
            </Panel>
          ))}
        </Collapse>
      )}
      </div>
    </div>
  );
}

export default Directory;
